import React, { useState, useRef, useEffect, useCallback } from "react";
import { Square, Volume2, Loader2, Truck, Calendar, ListChecks, BarChart3, Plus, Check, AlertTriangle, Clock, ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { motion } from "motion/react";
import { useStickToBottom } from "use-stick-to-bottom";
import { useVoiceAgent, VoiceAgentStatus, VoiceMessage, ConfirmationCardData } from "@/hooks/useVoiceAgent";
import { ExecutionPlanCard } from "@/components/ExecutionPlanCard";
import { ExecutionPlanEditMode } from "@/components/ExecutionPlanEditMode";
import { ReservationConfirmationCard } from "@/components/ReservationConfirmationCard";
import { OrderConfirmationCard } from "@/components/OrderConfirmationCard";
import { PendingOrderRecapCard } from "@/components/PendingOrderRecapCard";
import { useGlobalAgent } from "@/contexts/GlobalAgentContext";
import { useAuth } from "@/hooks/useAuth";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { VoiceRecordingInput, VoiceRecordingStatus } from "@/components/VoiceRecordingInput";
import { TypewriterText } from "@/components/TypewriterText";
import { useContextualSuggestions } from "@/hooks/useContextualSuggestions";
import type { ExecutionPlan } from "@/types/agent-events";
import { unlockAudio, unlockHtmlAudio, isHtmlAudioReady } from "@/lib/audioHelper";
import { useIsMobile } from "@/hooks/useMobile";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

// Messages already shown with typewriter (persists across panel open/close so we don't re-animate)
const animatedMessageKeys = new Set<string>();

function hashMessageKey(content: string): string {
  let h = 0;
  const len = Math.min(content.length, 500);
  for (let i = 0; i < len; i++) h = ((h << 5) - h + content.charCodeAt(i)) | 0;
  return `a:${h}`;
}

/** Détecte et découpe un message type "clarification" (intro + liste numérotée). */
function splitClarificationMessage(content: string): { intro: string; list: string } | null {
  if (!/informations\s+suivantes\s*[:\s]/i.test(content) || !/\n\s*1\.\s/m.test(content)) {
    return null;
  }
  const match = content.match(/\n\s*1\.\s/m);
  if (!match?.index) return null;
  return {
    intro: content.slice(0, match.index).trim(),
    list: content.slice(match.index).trim(),
  };
}

/** Échappe HTML pour affichage sûr. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Affiche le texte avec ** en gras (pas d’astérisques visibles). */
function formatMessageBold(content: string): React.ReactNode {
  const parts = content.split(/\*\*/g);
  if (parts.length === 1) return content;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} dangerouslySetInnerHTML={{ __html: escapeHtml(part) }} />
    ) : (
      <span key={i} dangerouslySetInnerHTML={{ __html: escapeHtml(part) }} />
    )
  );
}

interface UnifiedAgentChatProps {
  siteId: string | undefined;
  siteName: string | undefined;
  onClose?: () => void;
  onMinimize?: () => void;
  onStartSessionRef?: (fn: () => Promise<boolean>) => void;
  onEndSessionRef?: (fn: () => void) => void;
  className?: string;
  autoStartSession?: boolean;
  hideHeader?: boolean;
}

export function UnifiedAgentChat({
  siteId,
  siteName,
  onClose,
  onMinimize,
  onStartSessionRef,
  onEndSessionRef,
  className,
  autoStartSession = false,
  hideHeader = false,
}: UnifiedAgentChatProps) {
  const { scrollRef, contentRef } = useStickToBottom();
  const { setSessionActive } = useGlobalAgent();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [isEditing, setIsEditing] = useState(false);
  const [lastActionType, setLastActionType] = useState<string | undefined>(undefined);
  // Suggestions masquées après un clic jusqu'à la prochaine session
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);

  // Audio enable state for mobile (toggle)
  const [audioEnabled, setAudioEnabled] = useState(isHtmlAudioReady());
  
  // Handle toggling audio on mobile
  const handleToggleAudio = useCallback(() => {
    if (audioEnabled) {
      // Disable audio - just update state (we can't actually revoke permission)
      setAudioEnabled(false);
    } else {
      // Enable audio
      unlockAudio();
      unlockHtmlAudio();
      setAudioEnabled(true);
    }
  }, [audioEnabled]);

  const {
    status,
    messages,
    conversationId,
    isSessionActive,
    pendingExecutionPlan,
    confirmations,
    confirmationCard,
    pendingOrderRecap,
    pendingOrderInsertIndex,
    processingMessage,
    audioLevel,
    lastTranscription,
    mediaStream, // For passing to LiveWaveform (avoids duplicate getUserMedia on mobile)
    currentAudioElement, // For audio visualization during TTS playback
    startSession,
    endSession,
    startRecording,
    stopRecording,
    sendTextMessage,
    confirmPlan,
    cancelPlan,
    modifyPlan,
    dismissConfirmation,
    dismissConfirmationCard,
    dismissPendingOrderRecap,
    stopSpeaking,
    playAudio,
  } = useVoiceAgent({
    siteId,
    onSessionStart: () => setSessionActive(true),
    onSessionEnd: () => setSessionActive(false),
  });

  // Réafficher les suggestions au démarrage d'une nouvelle session
  const prevSessionActiveRef = useRef(isSessionActive);
  useEffect(() => {
    if (isSessionActive && !prevSessionActiveRef.current) {
      setSuggestionsDismissed(false);
    }
    prevSessionActiveRef.current = isSessionActive;
  }, [isSessionActive]);

  // Contextual suggestions
  const { suggestions } = useContextualSuggestions({ 
    siteId, 
    lastAction: lastActionType 
  });

  const animatedMessageKeysRef = useRef(animatedMessageKeys);
  
  // Update last action type from confirmations
  useEffect(() => {
    if (confirmations.length > 0) {
      const latest = confirmations[confirmations.length - 1];
      setLastActionType(latest.type);
    }
  }, [confirmations]);

  // Track if we're waiting for session to start before recording
  const pendingRecordingRef = useRef(false);
  // Capture press time before async session init to get accurate recording duration
  const pressStartTimeRef = useRef<number>(0);

  // Derive status flags early for use in effects
  const isRecording = status === "recording";
  const isProcessing = status === "processing";
  const isSpeaking = status === "speaking";

  // Recording duration timer state
  const [recordingDuration, setRecordingDuration] = useState(0);
  
  // Timer for recording duration
  useEffect(() => {
    if (!isRecording) {
      setRecordingDuration(0);
      return;
    }
    
    const startTime = Date.now();
    const interval = setInterval(() => {
      setRecordingDuration(Date.now() - startTime);
    }, 100);
    
    return () => clearInterval(interval);
  }, [isRecording]);

  // Note: useStickToBottom handles auto-scroll automatically

  // Expose session control functions to parent
  useEffect(() => {
    if (onStartSessionRef) {
      onStartSessionRef(startSession);
    }
    if (onEndSessionRef) {
      onEndSessionRef(endSession);
    }
  }, [onStartSessionRef, onEndSessionRef, startSession, endSession]);

  // Auto-start session if requested
  useEffect(() => {
    if (autoStartSession && siteId && !isSessionActive) {
      startSession();
    }
  }, [autoStartSession, siteId, isSessionActive, startSession]);

  // Tap-to-toggle handlers for WhatsApp-like recording
  const handleStartRecording = useCallback(async () => {
    if (!siteId) return;
    
    // Capture press time IMMEDIATELY before any async work
    pressStartTimeRef.current = Date.now();
    
    // If session is not active, start it first then record
    if (!isSessionActive) {
      pendingRecordingRef.current = true;
      const success = await startSession();
      
      if (success && pendingRecordingRef.current) {
        startRecording(pressStartTimeRef.current);
      }
      pendingRecordingRef.current = false;
      return;
    }
    
    // Session already active - pass the press time directly
    startRecording(pressStartTimeRef.current);
  }, [siteId, isSessionActive, startSession, startRecording]);

  const handleStopAndSend = useCallback(() => {
    pendingRecordingRef.current = false;
    stopRecording();
    // The audio is automatically sent via useVoiceAgent
  }, [stopRecording]);

  // Map voice agent status to VoiceRecordingInput status
  const voiceInputStatus: VoiceRecordingStatus = isRecording 
    ? "recording" 
    : isProcessing 
      ? "processing" 
      : isSpeaking 
        ? "speaking" 
        : "idle";
  
  // Get user's first name for personalized greeting
  const firstName = user?.first_name || "vous";

  return (
    <div className={cn("relative flex flex-col h-full bg-background", className)}>
      {/* Header - only show if not hidden */}
      {!hideHeader && (
        <div className="flex items-center justify-between px-md py-2 sm:px-lg sm:py-md border-b border-border shrink-0 bg-muted/30">
          <div className="flex items-center gap-sm sm:gap-md">
            <div className={cn(
              "w-2 h-2 rounded-full shrink-0",
              isSessionActive ? "bg-success" : "bg-muted-foreground/30"
            )} />
            
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-sm">
                <h3 className="text-card-title truncate">Agent IA</h3>
                {isSessionActive && (
                  <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-success/10 text-success text-body-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                    Actif
                  </span>
                )}
              </div>
              {siteName && (
                <p className="text-helper text-muted-foreground truncate max-w-[180px] sm:max-w-none">{siteName}</p>
              )}
            </div>
          </div>
          
          {/* Mobile audio toggle button */}
          {isMobile && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleAudio}
              className={cn(
                "text-body-sm gap-1.5 shrink-0",
                audioEnabled && "text-success border-success/50 hover:bg-success/10"
              )}
            >
              <Volume2 className="h-3.5 w-3.5" />
              {audioEnabled ? "Désactiver l'audio" : "Activer l'audio"}
            </Button>
          )}
        </div>
      )}

      {/* Messages area - custom scrollbar (non-native) */}
      <div 
        ref={scrollRef} 
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-md sm:px-lg agent-chat-scroll"
      >
        <div ref={contentRef} className="py-lg space-y-lg">
          {/* Welcome message when no messages */}
          {messages.length === 0 && (
            <div className="text-center py-xl">
              <div className="w-14 h-14 mx-auto mb-lg rounded-full bg-primary/10 flex items-center justify-center">
                <Volume2 className="w-7 h-7 text-primary" />
              </div>
              <h4 className="text-title mb-2">
                Bonjour {firstName}, comment puis-je vous aider ?
              </h4>
              <p className="text-body-md md:text-body-lg text-muted-foreground max-w-sm mx-auto mb-lg">
                Maintenez le bouton micro pour parler.
              </p>
              {suggestions.length > 0 && !suggestionsDismissed && (
                <div className="flex flex-wrap justify-center gap-sm mt-lg">
                  {suggestions.map((suggestion) => (
                    <SuggestionChip
                      key={suggestion.id}
                      label={suggestion.label}
                      icon={suggestion.icon}
                      onClick={() => {
                        sendTextMessage(suggestion.text);
                        setSuggestionsDismissed(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Message list with PendingOrderRecapCard inserted at the right position */}
          {messages.map((msg, idx) => {
            const isLastMessage = idx === messages.length - 1;
            const showQuickReplies = isLastMessage && msg.role === "assistant" && msg.suggestedReplies && msg.suggestedReplies.length > 0;
            
            // Check if we should show the pending order card after this message
            // pendingOrderInsertIndex is the length of messages at insertion time
            // So we show the card after the message at index (pendingOrderInsertIndex - 1)
            const showPendingOrderAfterThis = pendingOrderRecap && 
              pendingOrderInsertIndex !== null && 
              idx === pendingOrderInsertIndex - 1;
            
            return (
              <div key={msg.ts + idx}>
                <MessageBubble
                  message={msg}
                  isLatest={isLastMessage && msg.role === "assistant"}
                  confirmationCard={msg.showConfirmationCard ? confirmationCard : undefined}
                  onDismissCard={dismissConfirmationCard}
                  animatedKeysRef={animatedMessageKeysRef}
                  onPlayAudioWhenTypewriterStarts={playAudio}
                  conversationId={conversationId}
                  siteId={siteId}
                />
                
                {/* Quick reply bubbles after the last assistant message */}
                {showQuickReplies && (
                  <div className="flex flex-wrap gap-sm mt-2 ml-lg">
                    {msg.suggestedReplies!.map((reply, replyIdx) => (
                      <Button
                        key={replyIdx}
                        variant="outline"
                        size="sm"
                        onClick={() => sendTextMessage(reply)}
                        className="rounded-full text-body-md hover:bg-primary/10 hover:text-primary hover:border-primary/30"
                      >
                        {reply}
                      </Button>
                    ))}
                  </div>
                )}
                
                {/* Pending Order Recap Card - inserted at the correct position */}
                {showPendingOrderAfterThis && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start mt-lg max-w-full overflow-hidden"
                  >
                    <PendingOrderRecapCard
                      recap={pendingOrderRecap}
                      onConfirm={() => sendTextMessage("Envoyer la commande")}
                      onCancel={() => {
                        sendTextMessage("Annuler la commande");
                        dismissPendingOrderRecap();
                      }}
                      onAddTruckOptions={(truckNum) => sendTextMessage(`Ajouter des options sur le camion ${truckNum}`)}
                      onChangeFormula={() => sendTextMessage("Je veux modifier la formule béton")}
                      onModifyVolume={(idx) => sendTextMessage(`Modifier le volume de l'ouvrage ${idx + 1}`)}
                      onModifyWorkItem={(idx) => sendTextMessage(`Modifier l'ouvrage ${idx + 1}`)}
                      onAddWorkItem={() => sendTextMessage("Ajouter un ouvrage à la commande")}
                    />
                  </motion.div>
                )}
              </div>
            );
          })}

          {/* Suggestions après le dernier message (masquées après un clic jusqu'à nouvelle session) */}
          {messages.length > 0 && suggestions.length > 0 && !suggestionsDismissed && (
            <div className="flex flex-wrap gap-sm mt-lg">
              {suggestions.map((suggestion) => (
                <SuggestionChip
                  key={suggestion.id}
                  label={suggestion.label}
                  icon={suggestion.icon}
                  onClick={() => {
                    sendTextMessage(suggestion.text);
                    setSuggestionsDismissed(true);
                  }}
                />
              ))}
            </div>
          )}
          
          {/* Fallback: Show card at the end if no insert index or messages are empty */}
          {pendingOrderRecap && (pendingOrderInsertIndex === null || messages.length === 0) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start max-w-full overflow-hidden"
            >
              <PendingOrderRecapCard
                recap={pendingOrderRecap}
                onConfirm={() => sendTextMessage("Envoyer la commande")}
                onCancel={() => {
                  sendTextMessage("Annuler la commande");
                  dismissPendingOrderRecap();
                }}
                onAddTruckOptions={(truckNum) => sendTextMessage(`Ajouter des options sur le camion ${truckNum}`)}
                onChangeFormula={() => sendTextMessage("Je veux modifier la formule béton")}
                onModifyVolume={(idx) => sendTextMessage(`Modifier le volume de l'ouvrage ${idx + 1}`)}
                onModifyWorkItem={(idx) => sendTextMessage(`Modifier l'ouvrage ${idx + 1}`)}
                onAddWorkItem={() => sendTextMessage("Ajouter un ouvrage à la commande")}
              />
            </motion.div>
          )}

          {/* Pending execution plan - with edit mode */}
          {pendingExecutionPlan && !isEditing && (
            <ExecutionPlanCard
              plan={{
                ...pendingExecutionPlan,
                intent: pendingExecutionPlan.intent as any,
                inputs_normalized: pendingExecutionPlan.inputs_normalized as any,
                computed_values: pendingExecutionPlan.computed_values || {},
                risks: pendingExecutionPlan.risks || [],
                missing_fields: pendingExecutionPlan.missing_fields || [],
                requires_confirmation: true,
                next_tool_call: pendingExecutionPlan.next_tool_call as any,
                created_at: pendingExecutionPlan.expires_at,
              }}
              onConfirm={() => confirmPlan(pendingExecutionPlan.id)}
              onModify={() => setIsEditing(true)}
              onCancel={() => cancelPlan(pendingExecutionPlan.id)}
            />
          )}
          
          {/* Edit mode for execution plan */}
          {pendingExecutionPlan && isEditing && (
            <ExecutionPlanEditMode
              plan={{
                id: pendingExecutionPlan.id,
                intent: pendingExecutionPlan.intent as any,
                inputs_normalized: pendingExecutionPlan.inputs_normalized as any,
                computed_values: pendingExecutionPlan.computed_values || {},
                risks: pendingExecutionPlan.risks || [],
                missing_fields: pendingExecutionPlan.missing_fields || [],
                requires_confirmation: true,
                next_tool_call: pendingExecutionPlan.next_tool_call as any,
                created_at: pendingExecutionPlan.expires_at,
              }}
              onSave={async (modifiedPlan) => {
                await modifyPlan(pendingExecutionPlan.id, modifiedPlan.inputs_normalized);
                setIsEditing(false);
              }}
              onCancel={() => setIsEditing(false)}
            />
          )}

          {/* Transcription indicator - shows while processing voice */}
          {lastTranscription && isProcessing && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-end"
            >
              <div className="max-w-[85%] rounded-lg px-lg py-md bg-primary/20 text-primary-foreground/80 border border-primary/30">
                <div className="text-body-md md:text-body-lg whitespace-pre-wrap break-words">
                  "{lastTranscription}"
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span className="text-helper">Traitement...</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Speaking indicator */}
          {isSpeaking && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-sm text-muted-foreground"
            >
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-primary"
                    animate={{ y: [-2, 2, -2] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.1 }}
                  />
                ))}
              </div>
              <span className="text-body-md">L'agent parle...</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={stopSpeaking}
                className="h-6 px-2"
              >
                <Square className="h-3 w-3 mr-1" />
                Stop
              </Button>
            </motion.div>
          )}

          {/* Processing indicator with contextual message */}
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-sm text-muted-foreground"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-body-md">{processingMessage || "Traitement en cours..."}</span>
            </motion.div>
          )}

          {/* Spacer to keep content above the mic button */}
          <div className="h-36" aria-hidden="true" />
        </div>
      </div>

      {/* Floating Voice Recording Input - centered at bottom */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 pointer-events-auto">
        <VoiceRecordingInput
          status={voiceInputStatus}
          onStartRecording={handleStartRecording}
          onStopAndSend={handleStopAndSend}
          recordingDuration={recordingDuration}
          disabled={!siteId}
          mediaStream={mediaStream}
          audioElement={currentAudioElement}
        />
      </div>
    </div>
  );
}

// Suggestion chip component
function SuggestionChip({ 
  label, 
  icon, 
  onClick 
}: { 
  label: string; 
  icon?: string; 
  onClick: () => void;
}) {
  const IconComponent = icon === "truck" ? Truck 
    : icon === "calendar" ? Calendar 
    : icon === "list" ? ListChecks 
    : icon === "chart" ? BarChart3 
    : icon === "plus" ? Plus
    : icon === "check" ? Check
    : icon === "alert-triangle" ? AlertTriangle
    : icon === "clock" ? Clock
    : null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className="rounded-full text-body-md gap-1.5 hover:bg-primary/10 hover:text-primary hover:border-primary/30"
    >
      {IconComponent && <IconComponent className="w-3.5 h-3.5" />}
      {label}
    </Button>
  );
}

function MessageBubble({
  message,
  isLatest,
  confirmationCard,
  onDismissCard,
  animatedKeysRef,
  onTypewriterComplete,
  onPlayAudioWhenTypewriterStarts,
  conversationId,
  siteId,
}: {
  message: VoiceMessage;
  isLatest?: boolean;
  confirmationCard?: ConfirmationCardData | null;
  onDismissCard?: () => void;
  animatedKeysRef: React.MutableRefObject<Set<string>>;
  onTypewriterComplete?: (key: string) => void;
  onPlayAudioWhenTypewriterStarts?: (url: string) => void;
  conversationId?: string | null;
  siteId?: string;
}) {
  const isUser = message.role === "user";
  const [feedbackGiven, setFeedbackGiven] = useState<"positive" | "negative" | null>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const handleFeedback = async (rating: number) => {
    if (!conversationId || !siteId || feedbackGiven) return;
    setIsSubmittingFeedback(true);
    try {
      const { data: logs, error } = await supabase
        .from("agent_learning_logs")
        .select("id")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: false })
        .limit(1);

      if (error || !logs || logs.length === 0) return;

      const { error: updateError } = await supabase
        .from("agent_learning_logs")
        .update({ user_satisfaction: rating })
        .eq("id", logs[0].id);

      if (updateError) {
        logger.error("UnifiedAgentChat", "Error saving feedback:", updateError);
        return;
      }
      setFeedbackGiven(rating === 5 ? "positive" : "negative");
      toast.success("Merci !", { description: "Votre feedback aide à améliorer l'assistant." });
    } catch {
      toast.error("Erreur", { description: "Impossible d'envoyer votre feedback" });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };
  const messageKey = hashMessageKey(message.content);
  const alreadyAnimated = animatedKeysRef.current.has(messageKey);
  const shouldAnimate = !isUser && isLatest && !alreadyAnimated;
  if (!isUser && !shouldAnimate) animatedKeysRef.current.add(messageKey);
  const playOnStart = shouldAnimate && message.audioUrl && onPlayAudioWhenTypewriterStarts
    ? () => onPlayAudioWhenTypewriterStarts(message.audioUrl!)
    : undefined;

  const clarification = shouldAnimate ? splitClarificationMessage(message.content) : null;
  const [showListAfterIntro, setShowListAfterIntro] = useState(false);

  // If this message has a confirmation card, show it instead of text bubble
  if (message.showConfirmationCard && confirmationCard) {
    const isOrder = confirmationCard.type === 'order';
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-start"
      >
        <div className="max-w-[95%]">
          {isOrder ? (
            <OrderConfirmationCard 
              data={confirmationCard as any}
              onDismiss={onDismissCard}
            />
          ) : (
            <ReservationConfirmationCard 
              data={confirmationCard}
              onDismiss={onDismissCard}
            />
          )}
        </div>
      </motion.div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn("flex group", isUser ? "justify-end" : "justify-start")}
    >
        <div
          className={cn(
            "max-w-[85%] rounded-lg px-lg py-md shadow-sm",
            isUser
              ? "bg-primary text-primary-foreground rounded-br-md"
              : message.isError
                ? "bg-destructive/5 border border-destructive/30 rounded-bl-md"
                : "bg-muted rounded-bl-md"
          )}
      >
        {message.isError && (
          <div className="flex items-center gap-1.5 mb-1 text-destructive">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="text-body-sm">Erreur</span>
          </div>
        )}
        <div className="text-body-md md:text-body-lg whitespace-pre-wrap break-words overflow-wrap-anywhere">
          {shouldAnimate ? (
            clarification ? (
              <>
                <TypewriterText
                  text={clarification.intro}
                  speed={12}
                  onStart={playOnStart}
                  onComplete={() => {
                    setShowListAfterIntro(true);
                    animatedKeysRef.current.add(messageKey);
                    onTypewriterComplete?.(messageKey);
                  }}
                />
                {showListAfterIntro && (
                  <span className="block mt-2">{formatMessageBold(clarification.list)}</span>
                )}
              </>
            ) : (
              <TypewriterText
                text={message.content}
                speed={12}
                onStart={playOnStart}
                onComplete={() => {
                  animatedKeysRef.current.add(messageKey);
                  onTypewriterComplete?.(messageKey);
                }}
              />
            )
          ) : (
            formatMessageBold(message.content)
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <p className={cn(
            "text-helper",
            isUser ? "text-primary-foreground/70" : "text-muted-foreground"
          )}>
            {format(new Date(message.ts), "HH:mm", { locale: fr })}
          </p>

          {/* Feedback buttons - assistant messages only */}
          {!isUser && !feedbackGiven && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleFeedback(5)}
                disabled={isSubmittingFeedback}
                className="p-1 text-muted-foreground hover:text-success transition-colors disabled:opacity-50"
                title="Utile"
                aria-label="Utile"
              >
                <ThumbsUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleFeedback(1)}
                disabled={isSubmittingFeedback}
                className="p-1 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                title="Pas utile"
                aria-label="Pas utile"
              >
                <ThumbsDown className="w-3 h-3" />
              </button>
            </div>
          )}
          {feedbackGiven && (
            <span className={cn(
              "text-helper",
              feedbackGiven === "positive" ? "text-success" : "text-destructive"
            )}>
              {feedbackGiven === "positive" ? "👍" : "👎"}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
